import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Plus, Trash2, Loader2, Download, FileSpreadsheet, FileText as FileWord, FileText as FilePdf, Share2, Mail, MessageCircle, Sparkles, Pencil, Save, Camera, Printer, Upload } from 'lucide-react';
import InventoryPhotoCell from './InventoryPhotoCell';
import { exportInventoryExcel, exportInventoryPdf } from '../lib/inventoryExport';
import { printHtml } from '../lib/print';
import AiResearchDialog from './AiResearchDialog';
import InventoryScanDialog from './InventoryScanDialog';
import { buildSnapshotBranding } from '../lib/snapshotBranding';
import DocumentImportDialog from './DocumentImportDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHIPPING_OPTIONS = [
  { value: '',       label: '—' },
  { value: 'air',    label: 'Air freight' },
  { value: 'sea',    label: 'Sea freight' },
  { value: 'local',  label: 'Local source' },
];

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD' },
  { value: 'JMD', label: 'JMD' },
];

const COLUMNS = [
  { key: 'po_number',          label: 'PO #',                w: 'minmax(80px, 0.7fr)' },
  { key: 'markit_reference',   label: 'Markit ref',          w: 'minmax(90px, 0.8fr)' },
  { key: 'name',               label: 'Item name',           w: 'minmax(140px, 1.2fr)' },
  { key: 'quantity_on_hand',   label: 'Qty on hand',         w: 'minmax(80px, 0.7fr)', highlight: '#DCFCE7' },
  { key: 'quantity_requested', label: 'Qty requested',       w: 'minmax(90px, 0.7fr)', highlight: '#DBEAFE' },
  { key: 'unit_price',         label: 'Unit price',          w: 'minmax(90px, 0.8fr)', type: 'money' },
  { key: 'shipping_cost',      label: 'Shipping cost',       w: 'minmax(100px, 0.8fr)', type: 'money' },
  { key: 'tax_duty',           label: 'Tax / Duty',          w: 'minmax(90px, 0.8fr)', type: 'money' },
  { key: 'currency',           label: 'Currency',            w: 'minmax(80px, 0.7fr)', type: 'select', options: CURRENCY_OPTIONS },
  { key: 'description',        label: 'Description & specs', w: 'minmax(180px, 2fr)' },
  { key: 'brand',              label: 'Brand',               w: 'minmax(90px, 0.9fr)' },
  { key: 'vendor',             label: 'Vendor',              w: 'minmax(100px, 0.9fr)' },
  { key: 'vendor_reference',   label: 'Vendor ref',          w: 'minmax(90px, 0.8fr)' },
  { key: 'source_market',      label: 'Source market',       w: 'minmax(110px, 1fr)' },
  { key: 'lead_time',          label: 'Lead time',           w: 'minmax(90px, 0.8fr)' },
  { key: 'shipping_method',    label: 'Shipping',            w: 'minmax(110px, 0.9fr)', type: 'select', options: SHIPPING_OPTIONS },
  { key: 'freight_forwarder',  label: 'Freight forwarder',   w: 'minmax(120px, 1fr)' },
];

/* Excel-like inventory grid. Read-only for non-admins, fully editable for admins.
   Responsive: collapses to stacked cards on narrow screens. */
export default function InventoryTable({ nodeId, nodeLabel }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [lists, setLists] = useState([]);
  const [activeListId, setActiveListId] = useState('default');
  const [aiTarget, setAiTarget] = useState(null);
  const [renamingList, setRenamingList] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  /* Brand strip — populated lazily from /api/property + the matching F&B
     outlet when the node is a restaurant. Drives the header bar that sits
     above the document-name input. */
  const [brand, setBrand] = useState({
    hotel_name: 'Dreams Rose Hall Resort & Spa',
    hotel_subtitle: '',
    hotel_logo_url: '',
    hotel_operator_logo_url: '',
    restaurant_name: '',
    restaurant_logo_url: '',
    accent: '#003DA5',
  });
  /* Live, debounced "Document name" input — bound to the active list's
     name. Persists via PUT /admin/workspace/inventory-lists/{id} on
     blur / Enter so exports + snapshots always carry the latest title. */
  const [docNameDraft, setDocNameDraft] = useState('');
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const auth = adminToken ? { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true } : null;

  // Resolve property + restaurant branding once per node.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: prop } = await axios.get(`${API}/property`);
        if (cancelled) return;
        let restaurant = null;
        if (typeof nodeId === 'string' && nodeId.startsWith('restaurant.')) {
          const oid = nodeId.slice('restaurant.'.length);
          restaurant = (prop.fnb || []).find((o) => o.id === oid) || null;
        }
        setBrand({
          hotel_name: prop?.title || 'Dreams Rose Hall Resort & Spa',
          hotel_subtitle: prop?.subtitle || '',
          hotel_logo_url: prop?.brand_logo_url || '',
          hotel_operator_logo_url: prop?.operator_logo_url || '',
          restaurant_name: restaurant?.name || '',
          restaurant_logo_url: restaurant?.logo_url || '',
          accent: restaurant?.accent_color || '#003DA5',
        });
      } catch (_e) { /* hotel-only fallback already set */ }
    })();
    return () => { cancelled = true; };
  }, [nodeId]);

  const loadLists = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/workspace/nodes/${nodeId}/lists`, auth || {});
      setLists(data || []);
      if (data?.length && !data.find((l) => l.id === activeListId)) {
        setActiveListId(data[0].id);
      }
    } catch {
      setLists([{ id: 'default', name: 'Default list', node_id: nodeId, order: 0 }]);
    }
  }, [nodeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/workspace/nodes/${nodeId}/inventory`, {
        ...(auth || {}),
        params: { list_id: activeListId },
      });
      setRows(data);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [nodeId, activeListId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadLists(); }, [loadLists]);
  useEffect(() => { load(); }, [load]);

  const addRow = async () => {
    if (!auth) return;
    // CRITICAL for mobile: force-blur the currently-focused input BEFORE
    // POSTing the new row. iOS Safari can suppress the implicit `blur` when
    // a button steals focus during a touchend, which meant the user's
    // typed-but-unsaved text never reached the backend (the "items
    // disappear" bug). A synchronous blur here guarantees the previous
    // row's onSave fires first.
    if (typeof document !== 'undefined' && document.activeElement?.tagName === 'INPUT') {
      try { document.activeElement.blur(); } catch { /* no-op */ }
    }
    setCreating(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/workspace/inventory`,
        { node_id: nodeId, list_id: activeListId, order: rows.length },
        auth,
      );
      setRows((r) => [...r, data]);
    } finally {
      setCreating(false);
    }
  };

  // Saved-list ops
  const newList = async () => {
    if (!auth) return;
    const name = window.prompt('Name this saved list:', `List ${lists.length + 1}`);
    if (!name) return;
    const { data } = await axios.post(`${API}/admin/workspace/inventory-lists`, { node_id: nodeId, name }, auth);
    setLists((ls) => [...ls, data]);
    setActiveListId(data.id);
  };
  const renameList = async (lid, newName) => {
    if (!auth || !newName) return;
    await axios.put(`${API}/admin/workspace/inventory-lists/${lid}`, { name: newName }, auth);
    setLists((ls) => ls.map((l) => (l.id === lid ? { ...l, name: newName } : l)));
    setRenamingList(null);
  };
  const deleteList = async (lid) => {
    if (!auth) return;
    if (lid === 'default') return;
    if (!window.confirm('Permanently delete this list and ALL of its items? Pick Archive instead if you might want it back.')) return;
    await axios.delete(`${API}/admin/workspace/inventory-lists/${lid}`, auth);
    setLists((ls) => ls.filter((l) => l.id !== lid));
    if (activeListId === lid) setActiveListId('default');
  };
  // Archive — soft-hides the list and its rows. The list disappears
  // from the per-node lists endpoint and the aggregator (both filter
  // archived:true). Admin restores it from Admin → Archive.
  const archiveList = async (lid) => {
    if (!auth) return;
    if (lid === 'default') return;
    try {
      await axios.put(`${API}/admin/archive/inventory_list/${lid}`, {}, auth);
      setLists((ls) => ls.filter((l) => l.id !== lid));
      if (activeListId === lid) setActiveListId('default');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to archive');
    }
  };

  const updateCell = async (id, key, value) => {
    setRows((r) => r.map((x) => (x.id === id ? { ...x, [key]: value } : x)));
    if (!auth) return;
    try {
      await axios.put(`${API}/admin/workspace/inventory/${id}`, { [key]: value }, auth);
    } catch {
      load();
    }
  };

  const removeRow = async (id) => {
    if (!auth) return;
    if (!window.confirm('Delete this row?')) return;
    try {
      await axios.delete(`${API}/admin/workspace/inventory/${id}`, auth);
      setRows((r) => r.filter((x) => x.id !== id));
    } catch (err) { alert(err?.response?.data?.detail || 'Delete failed'); }
  };

  const exportCsv = () => {
    const header = COLUMNS.map((c) => c.label).join(',');
    const lines = rows.map((r) =>
      COLUMNS.map((c) => `"${String(r[c.key] ?? '').replace(/"/g, '""')}"`).join(','),
    );
    const blob = new Blob([header + '\n' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    triggerDownload(blob, `inventory-${nodeId}.csv`);
  };

  /* Snapshot save — captures the current inventory list (rows + active
     list metadata) as a frozen JSON payload on the server. Snapshot lives
     in the "My Files" header pill and is restorable as a new list.
     Branding (hotel + restaurant when scoped to a restaurant outlet) is
     stored inside the payload so the viewer + print can render a branded
     header without re-fetching property data. */
  const saveSnapshot = async () => {
    if (!auth) return;
    const defaultTitle = `${nodeLabel || nodeId} · ${new Date().toLocaleDateString()}`;
    const title = window.prompt('Name this saved snapshot:', defaultTitle);
    if (!title) return;
    try {
      const activeList = lists.find((l) => l.id === activeListId);
      // Restaurant-scoped lists carry `nodeId = "restaurant.{outletId}"` —
      // resolve the outlet for branding when present.
      let outlet = null;
      if (typeof nodeId === 'string' && nodeId.startsWith('restaurant.')) {
        try {
          const { data: prop } = await axios.get(`${API}/property`);
          const oid = nodeId.slice('restaurant.'.length);
          outlet = (prop.fnb || []).find((o) => o.id === oid) || null;
        } catch (_e) { /* branding falls back to hotel-only */ }
      }
      const branding = await buildSnapshotBranding(outlet);
      const payload = {
        rows: rows.map(({ id: _drop, node_id: _drop2, list_id: _drop3, created_at: _drop4, updated_at: _drop5, ...rest }) => rest),
        list: { name: activeList?.name || 'Default list' },
        node_label: nodeLabel,
        branding,
      };
      await axios.post(
        `${API}/admin/snapshots`,
        { kind: 'inventory', title, owner_node_id: nodeId, payload, shared: false },
        auth,
      );
      // Notify any open scoped saved-files list (RestaurantSavedFiles,
      // WorkspaceSavedFiles) to refresh without polling.
      window.dispatchEvent(new CustomEvent('snapshot-saved'));
      alert(`Saved "${title}" to My Files. Open it any time from the green folder pill in the header.`);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save snapshot');
    }
  };

  /* Restore a snapshot fired from SavedFilesDialog. We materialise the
     frozen payload as a brand-new saved list named after the snapshot, so
     the live list the user was on stays untouched. */
  useEffect(() => {
    const onSnap = async (e) => {
      const snap = e?.detail;
      if (!snap || snap.kind !== 'inventory') return;
      // Only restore if this table belongs to the snapshot's node.
      if (snap.owner_node_id && snap.owner_node_id !== nodeId) return;
      if (!auth) return;
      try {
        const restoredListName = `${snap.title} (restored)`;
        const { data: list } = await axios.post(
          `${API}/admin/workspace/inventory-lists`,
          { node_id: nodeId, name: restoredListName },
          auth,
        );
        const snapRows = snap.payload?.rows || [];
        for (let i = 0; i < snapRows.length; i++) {
          await axios.post(
            `${API}/admin/workspace/inventory`,
            { node_id: nodeId, list_id: list.id, order: i, ...snapRows[i] },
            auth,
          );
        }
        setLists((ls) => [...ls, list]);
        setActiveListId(list.id);
        alert(`Restored "${snap.title}" as new list "${restoredListName}".`);
      } catch (err) {
        alert(err?.response?.data?.detail || 'Restore failed');
      }
    };
    window.addEventListener('open-snapshot', onSnap);
    return () => window.removeEventListener('open-snapshot', onSnap);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [nodeId, auth, lists, activeListId]);

  const exportExcel = async () => {
    try {
      await exportInventoryExcel({ rows, nodeId, nodeLabel });
    } catch (e) {
      alert('Excel export failed: ' + (e?.message || 'unknown error'));
    }
  };

  const exportWord = () => {
    // Minimal Word-compatible HTML — Word natively opens .doc with HTML inside.
    // The `@page` block tells Word to print in LANDSCAPE A4 with tight
    // margins; `table-layout: fixed` + `word-wrap: break-word` keeps every
    // column visible on a single printed page width (no horizontal scroll
    // or page break). Header rows repeat on each page via `<thead>`.
    const colWidthPct = (100 / (COLUMNS.length)).toFixed(2);
    const tableHtml = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head>
        <meta charset='utf-8'>
        <title>Inventory</title>
        <!--[if gte mso 9]><xml>
          <w:WordDocument>
            <w:View>Print</w:View>
            <w:Zoom>90</w:Zoom>
            <w:DoNotOptimizeForBrowser/>
          </w:WordDocument>
        </xml><![endif]-->
        <style>
          @page Section1 { size: 29.7cm 21cm; mso-page-orientation: landscape; margin: 1cm 0.6cm; }
          div.Section1 { page: Section1; }
          body { font-family: Calibri, Arial, sans-serif; font-size: 9px; }
          h2 { font-family: Calibri, Arial, sans-serif; color: #003DA5; margin: 0 0 8px 0; font-size: 14px; }
          table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 8px; }
          th, td { border: 1px solid #C9D6E6; padding: 3px 4px; vertical-align: top; word-wrap: break-word; overflow-wrap: anywhere; }
          thead tr { background:#003DA5; color:white; }
          th { text-align: left; }
          tbody tr:nth-child(even) td { background: #F4F8FD; }
        </style>
      </head>
      <body>
        <div class='Section1'>
          <h2>${nodeLabel || nodeId} — Inventory</h2>
          <table>
            <thead><tr>${COLUMNS.map((c) => `<th style='width:${colWidthPct}%'>${c.label}</th>`).join('')}</tr></thead>
            <tbody>${rows.map((r) => `<tr>${COLUMNS.map((c) => `<td>${String(r[c.key] ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;')}</td>`).join('')}</tr>`).join('')}</tbody>
          </table>
        </div>
      </body></html>`;
    const blob = new Blob(['\ufeff' + tableHtml], { type: 'application/msword' });
    triggerDownload(blob, `inventory-${nodeId}.doc`);
  };

  const exportPdf = async () => {
    try {
      await exportInventoryPdf({ rows, nodeId, nodeLabel });
    } catch (e) {
      alert('PDF export failed: ' + (e?.message || 'unknown error'));
    }
  };

  // Direct printer flow — no file download, just `window.print()` of a
  // formatted HTML table. Reuses the shared `printHtml` helper so the
  // landscape A4 styling matches every other printable in the app
  // (snapshots, schedules, restaurants, menus).
  const printList = () => {
    const activeList = lists.find((l) => l.id === activeListId);
    const listLabel = activeList?.name || nodeLabel || 'Inventory';
    const colWidthPct = (100 / (COLUMNS.length)).toFixed(2);
    const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const body = `
      <p class="kicker">DREAMS ROSE HALL · INVENTORY</p>
      <h1>${esc(listLabel)}</h1>
      <p class="meta">${rows.length} row${rows.length === 1 ? '' : 's'} · Printed ${esc(new Date().toLocaleString())}</p>
      <table>
        <thead><tr>${COLUMNS.map((c) => `<th style="width:${colWidthPct}%">${esc(c.label)}</th>`).join('')}</tr></thead>
        <tbody>${rows.map((r) => `<tr>${COLUMNS.map((c) => `<td>${esc(r[c.key] ?? '')}</td>`).join('')}</tr>`).join('')}</tbody>
      </table>
    `;
    printHtml(body, `${listLabel} — Dreams Rose Hall`);
  };

  const triggerDownload = (blob, filename) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // ---------------- Share helpers ----------------
  const buildShareText = () => {
    const title = nodeLabel ? `${nodeLabel} — Inventory` : `Inventory (${nodeId})`;
    const lines = rows.slice(0, 25).map((r) => {
      const qty = r.quantity ? ` · Qty ${r.quantity}` : '';
      const brand = r.brand ? ` · ${r.brand}` : '';
      return `• ${r.name || '(unnamed)'}${brand}${qty}`;
    });
    const more = rows.length > 25 ? `\n…and ${rows.length - 25} more.` : '';
    const url = typeof window !== 'undefined' ? window.location.href : '';
    return `${title}\n${rows.length} items\n\n${lines.join('\n')}${more}\n\nView the full list: ${url}`;
  };

  const shareEmail = () => {
    const subject = nodeLabel ? `Inventory — ${nodeLabel}` : 'Inventory list';
    const body = buildShareText();
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const shareWhatsApp = () => {
    const text = buildShareText();
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  const shareNative = async () => {
    const text = buildShareText();
    const title = nodeLabel ? `Inventory — ${nodeLabel}` : 'Inventory list';
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url: window.location.href });
      }
    } catch { /* user cancelled */ }
  };

  const [exportOpen, setExportOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  useEffect(() => {
    const onDocClick = () => { setExportOpen(false); setShareOpen(false); };
    if (exportOpen || shareOpen) document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [exportOpen, shareOpen]);

  // Keep the editable Document-name input in sync with the active list's name.
  const activeList = lists.find((l) => l.id === activeListId) || null;
  useEffect(() => { setDocNameDraft(activeList?.name || ''); }, [activeList?.id, activeList?.name]);

  /* Persist the draft name back to the active list. Called on Enter / blur.
     No-ops when the value is empty or unchanged. */
  const commitDocName = () => {
    const next = (docNameDraft || '').trim();
    if (!activeList) return;
    if (!next || next === activeList.name) {
      setDocNameDraft(activeList.name || '');
      return;
    }
    renameList(activeList.id, next);
  };

  const isRestaurant = typeof nodeId === 'string' && nodeId.startsWith('restaurant.');
  const accent = brand.accent || '#003DA5';

  return (
    <div data-testid={`inventory-table-${nodeId}`} className="w-full">
      {/* Document header — hotel branding strip (always) + restaurant branding
          (when scope is `restaurant.{id}`) + an editable Document name input.
          The name flows through to every export (PDF / Excel / Word / Print)
          and to the snapshot title via `activeList.name`. */}
      <div
        className="mb-4 rounded-2xl overflow-hidden"
        data-testid="inventory-doc-header"
        style={{
          background: `linear-gradient(135deg, ${accent}12 0%, ${accent}04 100%)`,
          border: `1px solid ${accent}30`,
        }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3" style={{ borderBottom: `1px solid ${accent}20` }}>
          <div className="flex items-center gap-2 min-w-0">
            {brand.hotel_logo_url && (
              <img src={brand.hotel_logo_url.startsWith('http') ? brand.hotel_logo_url : `${process.env.REACT_APP_BACKEND_URL}${brand.hotel_logo_url}`} alt="" className="h-9 w-auto max-w-[80px] object-contain rounded-md" style={{ background: 'white', padding: 2 }} />
            )}
            <div className="min-w-0">
              <p className="text-[9px] font-bold tracking-[0.25em] uppercase truncate" style={{ color: accent, fontFamily: 'Manrope' }}>
                {brand.hotel_name}
              </p>
              {brand.hotel_subtitle && (
                <p className="text-[10px] truncate" style={{ color: '#64748B', fontFamily: 'Manrope' }}>{brand.hotel_subtitle}</p>
              )}
            </div>
          </div>
          {/* Restaurant chip + logo on the right when scoped to a restaurant */}
          {isRestaurant && brand.restaurant_name && (
            <div className="sm:ml-auto flex items-center gap-2" data-testid="inventory-doc-restaurant-brand">
              <div className="text-right min-w-0">
                <p className="text-[9px] font-bold tracking-[0.25em] uppercase" style={{ color: accent, fontFamily: 'Manrope' }}>Restaurant</p>
                <p className="text-sm font-bold truncate" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{brand.restaurant_name}</p>
              </div>
              {brand.restaurant_logo_url && (
                <img src={brand.restaurant_logo_url.startsWith('http') ? brand.restaurant_logo_url : `${process.env.REACT_APP_BACKEND_URL}${brand.restaurant_logo_url}`} alt={brand.restaurant_name} className="h-12 w-12 object-contain rounded-lg" style={{ background: 'white', padding: 4, border: `1px solid ${accent}30` }} />
              )}
            </div>
          )}
        </div>

        {/* Document name input — prominent, always visible. Persists on
            blur / Enter so prints + exports + snapshots use it. */}
        <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2">
          <label className="text-[10px] font-bold tracking-[0.25em] uppercase flex-shrink-0" style={{ color: accent, fontFamily: 'Manrope' }} htmlFor="inventory-doc-name-input">
            Document name
          </label>
          <input
            id="inventory-doc-name-input"
            type="text"
            value={docNameDraft}
            onChange={(e) => setDocNameDraft(e.target.value)}
            onBlur={commitDocName}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setDocNameDraft(activeList?.name || ''); e.currentTarget.blur(); } }}
            placeholder={nodeLabel ? `${nodeLabel} document` : 'Untitled document'}
            disabled={!adminToken}
            data-testid="inventory-doc-name"
            className="flex-1 px-3 py-2 rounded-lg text-base font-bold outline-none transition-colors"
            style={{
              background: 'white',
              color: '#1A2E2E',
              border: `1px solid ${accent}40`,
              fontFamily: "'Cormorant Garamond', serif",
            }}
          />
          {adminToken && activeList && (
            <span className="text-[10px]" style={{ color: '#64748B', fontFamily: 'Manrope' }}>
              press Enter to save
            </span>
          )}
        </div>
      </div>

      {/* Saved-list tabs — every node has at least one (Default). Admins can
          create new buckets (e.g. "Q1 PO 2026", "Opening inventory") and
          rename/delete existing ones. Non-admins just see the tab strip. */}
      {lists.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="saved-lists-tabs">
          {lists.map((l) => {
            const active = l.id === activeListId;
            const isRenaming = renamingList === l.id;
            return (
              <div key={l.id} className="relative">
                {isRenaming ? (
                  <input
                    autoFocus
                    defaultValue={l.name}
                    onBlur={(e) => renameList(l.id, e.target.value.trim())}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setRenamingList(null); }}
                    className="px-3 py-1.5 rounded-full text-[11px] font-bold outline-none"
                    style={{ backgroundColor: 'white', color: '#003DA5', border: '2px solid #003DA5', fontFamily: 'Manrope', minWidth: 120 }}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setActiveListId(l.id)}
                    onDoubleClick={() => adminToken && setRenamingList(l.id)}
                    title={adminToken ? 'Click to open · Double-click to rename' : ''}
                    className="px-3 py-1.5 rounded-full text-[11px] font-bold transition-all"
                    style={{
                      backgroundColor: active ? '#003DA5' : 'white',
                      color: active ? 'white' : '#003DA5',
                      border: '1px solid #003DA5',
                      fontFamily: 'Manrope',
                    }}
                    data-testid={`saved-list-${l.id}`}
                  >
                    {l.name}
                  </button>
                )}
                {adminToken && active && l.id !== 'default' && !isRenaming && (
                  <>
                    <button
                      type="button"
                      onClick={() => archiveList(l.id)}
                      className="ml-1 w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px] font-bold"
                      style={{ backgroundColor: '#FEF3C7', color: '#B45309' }}
                      title="Archive list — restore later from Admin → Archive"
                      data-testid={`saved-list-archive-${l.id}`}
                    >▼</button>
                    <button
                      type="button"
                      onClick={() => deleteList(l.id)}
                      className="ml-1 w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px] font-bold"
                      style={{ backgroundColor: '#FEE2E2', color: '#B91C1C' }}
                      title="Permanently delete list"
                      data-testid={`saved-list-delete-${l.id}`}
                    >×</button>
                  </>
                )}
              </div>
            );
          })}
          {adminToken && (
            <button
              type="button"
              onClick={newList}
              className="px-3 py-1.5 rounded-full text-[11px] font-bold flex items-center gap-1 transition-all hover:-translate-y-0.5"
              style={{ backgroundColor: 'white', color: '#003DA5', border: '1px dashed #003DA5', fontFamily: 'Manrope' }}
              data-testid="saved-list-add"
            >
              <Plus className="w-3 h-3" /> New list
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="text-[11px] font-bold tracking-[0.15em] uppercase" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>
          {rows.length} {rows.length === 1 ? 'item' : 'items'}
        </div>
        <div className="flex-1" />
        {/* Share — always available, even when empty (with empty-state message) */}
        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setShareOpen((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:-translate-y-0.5"
            style={{ backgroundColor: '#003DA5', color: 'white', fontFamily: 'Manrope' }}
            data-testid="inventory-share-btn"
            title="Share this inventory"
          >
            <Share2 className="w-3 h-3" /> Share
            <span className="text-[8px] opacity-70">▾</span>
          </button>
          {shareOpen && (
            <div
              className="absolute right-0 mt-1 z-50 rounded-lg overflow-hidden"
              style={{ backgroundColor: 'white', border: '1px solid #D9E3F1', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 180 }}
              data-testid="inventory-share-menu"
            >
              <ExportItem label="Email" icon={<Mail className="w-3.5 h-3.5" />} onClick={() => { shareEmail(); setShareOpen(false); }} testid="share-email" color="#1F4FBE" />
              <ExportItem label="WhatsApp" icon={<MessageCircle className="w-3.5 h-3.5" />} onClick={() => { shareWhatsApp(); setShareOpen(false); }} testid="share-whatsapp" color="#16A34A" />
              {typeof navigator !== 'undefined' && navigator.share && (
                <ExportItem label="More…" icon={<Share2 className="w-3.5 h-3.5" />} onClick={() => { shareNative(); setShareOpen(false); }} testid="share-native" color="#003DA5" />
              )}
            </div>
          )}
        </div>
        {rows.length > 0 && (
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setExportOpen((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:-translate-y-0.5"
              style={{ backgroundColor: 'white', color: '#003DA5', border: '1px solid #A3C4EE', fontFamily: 'Manrope' }}
              data-testid="inventory-export-btn"
            >
              <Download className="w-3 h-3" /> Export
              <span className="text-[8px] opacity-70">▾</span>
            </button>
            {exportOpen && (
              <div
                className="absolute right-0 mt-1 z-50 rounded-lg overflow-hidden"
                style={{ backgroundColor: 'white', border: '1px solid #D9E3F1', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 160 }}
                data-testid="inventory-export-menu"
              >
                <ExportItem label="Excel (.xlsx)" icon={<FileSpreadsheet className="w-3.5 h-3.5" />} onClick={() => { exportExcel(); setExportOpen(false); }} testid="export-xlsx" color="#16A34A" />
                <ExportItem label="Word (.doc)" icon={<FileWord className="w-3.5 h-3.5" />} onClick={() => { exportWord(); setExportOpen(false); }} testid="export-doc" color="#1F4FBE" />
                <ExportItem label="PDF (.pdf)" icon={<FilePdf className="w-3.5 h-3.5" />} onClick={() => { exportPdf(); setExportOpen(false); }} testid="export-pdf" color="#DC2626" />
                <ExportItem label="CSV (.csv)" icon={<Download className="w-3.5 h-3.5" />} onClick={() => { exportCsv(); setExportOpen(false); }} testid="export-csv" color="#7A8C8C" />
              </div>
            )}
          </div>
        )}
        {rows.length > 0 && (
          <button
            onClick={printList}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white transition-all hover:-translate-y-0.5"
            style={{ backgroundColor: '#0A1A2F', fontFamily: 'Manrope' }}
            data-testid="inventory-print"
            title="Print this list"
          >
            <Printer className="w-3 h-3" /> Print
          </button>
        )}
        {adminToken && rows.length > 0 && (
          <button
            onClick={saveSnapshot}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:-translate-y-0.5"
            style={{ backgroundColor: '#22C55E20', color: '#15803D', border: '1px solid #BBF7D0', fontFamily: 'Manrope' }}
            data-testid="inventory-save-snapshot"
            title="Save the current list as a snapshot in My Files"
          >
            <Save className="w-3 h-3" /> Save snapshot
          </button>
        )}
        {adminToken && (
          <button
            onClick={() => setImportOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white transition-all hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #1F4FBE 0%, #1E40AF 100%)', fontFamily: 'Manrope' }}
            data-testid="inventory-import-btn"
            title="Import PDF / Word / Excel / CSV into this inventory"
          >
            <Upload className="w-3 h-3" /> Import file
          </button>
        )}
        {adminToken && (
          <button
            onClick={() => setScanOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)', color: 'white', fontFamily: 'Manrope' }}
            data-testid="inventory-scan-open"
            title="Scan a printed or handwritten OS&E list into rows"
          >
            <Camera className="w-3 h-3" /> Scan list
          </button>
        )}
        {adminToken && (
          <button
            onClick={addRow}
            disabled={creating}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50"
            style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }}
            data-testid="inventory-add-row"
          >
            {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Add item
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-sm rounded-xl" style={{ backgroundColor: 'white', border: '1px dashed #A3C4EE', color: '#7A8C8C', fontFamily: 'Manrope' }}>
          {adminToken ? 'No items yet. Click “Add item” to create the first row.' : 'No items recorded yet.'}
        </div>
      ) : (
        <>
          {/* Desktop grid — horizontally scrollable since we have 13+ columns */}
          <div className="hidden md:block rounded-xl overflow-x-auto" style={{ border: '1px solid #D9E3F1', backgroundColor: 'white' }}>
            <div style={{ minWidth: 1760 }}>
            {/* Header */}
            <div
              className="grid text-[10px] font-bold tracking-[0.12em] uppercase"
              style={{
                gridTemplateColumns: '64px ' + COLUMNS.map((c) => c.w).join(' ') + (adminToken ? ' 36px 36px' : ''),
                backgroundColor: '#003DA5',
                color: 'white',
                fontFamily: 'Manrope',
              }}
            >
              <div className="px-3 py-2.5 border-r border-white/10">Photo</div>
              {COLUMNS.map((c) => (
                <div key={c.key} className="px-3 py-2.5 border-r border-white/10" title={c.label}>{c.label}</div>
              ))}
              {adminToken && <div className="px-2 py-2.5 text-center" title="AI deep research">AI</div>}
              {adminToken && <div className="px-2 py-2.5" />}
            </div>
            {rows.map((row, idx) => (
              <div
                key={row.id}
                className="grid text-xs hover:bg-blue-50/40 transition-colors"
                style={{
                  gridTemplateColumns: '64px ' + COLUMNS.map((c) => c.w).join(' ') + (adminToken ? ' 36px 36px' : ''),
                  borderTop: idx === 0 ? 'none' : '1px solid #EEF3F9',
                  fontFamily: 'Manrope',
                  color: '#1A2E2E',
                }}
                data-testid={`inventory-row-${row.id}`}
              >
                <div className="flex items-center justify-center px-2 py-1.5 border-r border-[#EEF3F9]">
                  <InventoryPhotoCell
                    value={row.photo_url || ''}
                    editable={!!adminToken}
                    onChange={(v) => updateCell(row.id, 'photo_url', v)}
                    token={adminToken}
                    size={48}
                    testId={`inventory-photo-${row.id}`}
                  />
                </div>
                {COLUMNS.map((c) => (
                  <InventoryCell
                    key={c.key}
                    value={row[c.key] || ''}
                    editable={!!adminToken}
                    onSave={(v) => updateCell(row.id, c.key, v)}
                    highlight={c.highlight}
                    type={c.type}
                    options={c.options}
                  />
                ))}
                {adminToken && (
                  <button
                    onClick={() => setAiTarget(row)}
                    className="flex items-center justify-center transition-colors hover:bg-blue-50"
                    title="AI deep research vendors"
                    data-testid={`inventory-ai-${row.id}`}
                    style={{ color: '#003DA5' }}
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                  </button>
                )}
                {adminToken && (
                  <button
                    onClick={() => removeRow(row.id)}
                    className="flex items-center justify-center text-[#EF4444] hover:bg-red-50 transition-colors"
                    title="Delete row"
                    data-testid={`inventory-delete-${row.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            {/* Totals row — sums numeric columns across all rows so admins
                see at-a-glance stock levels without exporting. Numeric
                parse tolerates "12 pcs", "≈5", etc. by stripping
                non-numeric characters before summing. */}
            {rows.length > 0 && (
              <div
                data-testid="inventory-totals-row"
                className="grid text-xs"
                style={{
                  gridTemplateColumns: '64px ' + COLUMNS.map((c) => c.w).join(' ') + (adminToken ? ' 36px 36px' : ''),
                  borderTop: '2px solid #003DA5',
                  backgroundColor: '#F0F4FA',
                  fontFamily: 'Manrope',
                  color: '#003DA5',
                  fontWeight: 700,
                }}
              >
                <div className="px-3 py-2 border-r border-[#EEF3F9] text-[10px] uppercase tracking-[0.12em]">
                  Totals · {rows.length} {rows.length === 1 ? 'item' : 'items'}
                </div>
                {COLUMNS.map((c) => {
                  const numericKeys = ['quantity_on_hand', 'quantity_requested', 'unit_price', 'shipping_cost', 'tax_duty'];
                  if (numericKeys.includes(c.key)) {
                    // iter-79: render the total in the SAME unit as the
                    // column. Money columns (unit_price / shipping_cost
                    // / tax_duty — flagged via `c.type === 'money'`) get
                    // a currency prefix; quantity columns render a
                    // plain integer count regardless of what the row's
                    // `currency` field happens to be. This fixes the
                    // "all totals show as $" bug where a stock count of
                    // 50 glasses was rendering as "$50".
                    const isMoney = c.type === 'money';
                    if (isMoney) {
                      // Split totals per currency so $100 USD + ¥100
                      // JMD never get summed into one nonsensical
                      // "$200". Collapse to a single currency only if
                      // every row matches.
                      const byCurrency = rows.reduce((acc, r) => {
                        const raw = String(r[c.key] ?? '').replace(/[^\d.-]/g, '');
                        const n = parseFloat(raw);
                        if (!Number.isFinite(n)) return acc;
                        const cur = r.currency || 'USD';
                        acc[cur] = (acc[cur] || 0) + n;
                        return acc;
                      }, {});
                      const entries = Object.entries(byCurrency);
                      let display = '';
                      const fmt = (n) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      if (entries.length === 1) {
                        const [cur, total] = entries[0];
                        display = `${cur === 'JMD' ? 'J$' : '$'}${fmt(total)}`;
                      } else if (entries.length > 1) {
                        display = entries.map(([cur, total]) => `${cur === 'JMD' ? 'J$' : '$'}${fmt(total)}`).join(' · ');
                      } else {
                        display = '—';
                      }
                      return (
                        <div
                          key={c.key}
                          className="px-3 py-2 border-r border-[#EEF3F9] text-right tabular-nums whitespace-nowrap"
                          style={{ backgroundColor: c.highlight || 'transparent' }}
                          data-testid={`inventory-total-${c.key}`}
                        >
                          {display}
                        </div>
                      );
                    }
                    // Quantity column → plain unitless count. The label
                    // ("glasses", "linens", "tools" etc.) lives on the
                    // column header so the total just shows the number.
                    const total = rows.reduce((acc, r) => {
                      const raw = String(r[c.key] ?? '').replace(/[^\d.-]/g, '');
                      const n = parseFloat(raw);
                      return Number.isFinite(n) ? acc + n : acc;
                    }, 0);
                    return (
                      <div
                        key={c.key}
                        className="px-3 py-2 border-r border-[#EEF3F9] text-right tabular-nums whitespace-nowrap"
                        style={{ backgroundColor: c.highlight || 'transparent' }}
                        data-testid={`inventory-total-${c.key}`}
                      >
                        {total > 0 ? total.toLocaleString() : '—'}
                      </div>
                    );
                  }
                  return <div key={c.key} className="px-3 py-2 border-r border-[#EEF3F9]" />;
                })}
                {adminToken && <div />}
                {adminToken && <div />}
              </div>
            )}
            </div>
          </div>

          {/* Mobile stacked cards */}
          <div className="md:hidden space-y-3">
            {rows.map((row) => (
              <div key={row.id} className="rounded-xl p-3" style={{ backgroundColor: 'white', border: '1px solid #D9E3F1' }}>
                <div className="flex items-start gap-3 mb-2">
                  <InventoryPhotoCell
                    value={row.photo_url || ''}
                    editable={!!adminToken}
                    onChange={(v) => updateCell(row.id, 'photo_url', v)}
                    token={adminToken}
                    size={64}
                    testId={`inventory-photo-mobile-${row.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] font-bold tracking-[0.15em] uppercase mb-0.5" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>Photo</div>
                    <div className="text-[10px] opacity-70" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                      {adminToken ? 'Tap to take or upload a photo' : (row.photo_url ? 'Tap photo to view full size' : 'No photo')}
                    </div>
                  </div>
                </div>
                {COLUMNS.map((c) => (
                  <div key={c.key} className="mb-2 last:mb-0" data-testid={`inventory-cell-mobile-${row.id}-${c.key}`}>
                    <div className="text-[9px] font-bold tracking-[0.15em] uppercase mb-0.5" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>{c.label}</div>
                    <InventoryCell
                      value={row[c.key] || ''}
                      editable={!!adminToken}
                      onSave={(v) => updateCell(row.id, c.key, v)}
                      mobile
                      highlight={c.highlight}
                      type={c.type}
                      options={c.options}
                      testid={`inventory-input-mobile-${row.id}-${c.key}`}
                    />
                  </div>
                ))}
                {adminToken && (
                  <div className="mt-2 flex items-center gap-3">
                    <button
                      onClick={() => setAiTarget(row)}
                      className="text-[10px] font-bold flex items-center gap-1"
                      style={{ color: '#003DA5', fontFamily: 'Manrope' }}
                    >
                      <Sparkles className="w-3 h-3" /> AI research
                    </button>
                    <button
                      onClick={() => removeRow(row.id)}
                      className="text-[10px] font-bold flex items-center gap-1"
                      style={{ color: '#EF4444', fontFamily: 'Manrope' }}
                    >
                      <Trash2 className="w-3 h-3" /> Delete row
                    </button>
                  </div>
                )}
              </div>
            ))}
            {/* Mobile totals card — same numeric parser as the desktop
                totals row; only renders when at least one item exists. */}
            {rows.length > 0 && (() => {
              const onHand = rows.reduce((s, r) => {
                const n = parseFloat(String(r.quantity_on_hand ?? '').replace(/[^\d.-]/g, ''));
                return s + (Number.isFinite(n) ? n : 0);
              }, 0);
              const requested = rows.reduce((s, r) => {
                const n = parseFloat(String(r.quantity_requested ?? '').replace(/[^\d.-]/g, ''));
                return s + (Number.isFinite(n) ? n : 0);
              }, 0);
              return (
                <div
                  data-testid="inventory-totals-mobile"
                  className="rounded-xl p-3"
                  style={{
                    backgroundColor: '#F0F4FA',
                    border: '2px solid #003DA5',
                    fontFamily: 'Manrope',
                    color: '#003DA5',
                  }}
                >
                  <div className="text-[10px] font-bold tracking-[0.15em] uppercase mb-2">
                    Totals · {rows.length} {rows.length === 1 ? 'item' : 'items'}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-lg p-2" style={{ backgroundColor: '#DCFCE7' }}>
                      <div className="text-[9px] uppercase tracking-wider opacity-80">Qty on hand</div>
                      <div className="text-base font-bold tabular-nums" data-testid="inventory-total-mobile-on-hand">
                        {onHand.toLocaleString()}
                      </div>
                    </div>
                    <div className="rounded-lg p-2" style={{ backgroundColor: '#DBEAFE' }}>
                      <div className="text-[9px] uppercase tracking-wider opacity-80">Qty requested</div>
                      <div className="text-base font-bold tabular-nums" data-testid="inventory-total-mobile-requested">
                        {requested.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </>
      )}

      {/* AI Deep Research dialog mount — single instance shared by every row */}
      {aiTarget && (
        <AiResearchDialog
          open={!!aiTarget}
          onClose={() => setAiTarget(null)}
          item={aiTarget}
          token={adminToken}
          onApply={async (patch) => {
            try {
              await axios.put(`${API}/admin/workspace/inventory/${aiTarget.id}`, patch, auth);
              setRows((rs) => rs.map((r) => (r.id === aiTarget.id ? { ...r, ...patch } : r)));
            } catch { /* no-op */ }
          }}
        />
      )}

      <InventoryScanDialog
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        nodeId={nodeId}
        listId={activeListId}
        listLabel={(lists.find((l) => l.id === activeListId) || {}).name || nodeLabel || 'this list'}
        onComplete={() => { load(); }}
      />

      {/* Universal document import — converts PDF / Word / Excel / CSV
          into Atrium inventory rows. */}
      <DocumentImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        targetType="inventory"
        activeListName={activeList?.name || ''}
        newNameDefault={`Imported · ${new Date().toLocaleDateString()}`}
        onCommit={async (importedRows, { mode, newName }) => {
          if (!auth) return;
          let listId = activeListId;
          if (mode === 'new') {
            const { data: list } = await axios.post(
              `${API}/admin/workspace/inventory-lists`,
              { node_id: nodeId, name: newName || `Imported · ${new Date().toLocaleDateString()}` },
              auth,
            );
            setLists((ls) => [...ls, list]);
            listId = list.id;
            setActiveListId(list.id);
          }
          // Insert rows sequentially so the order matches the import.
          const baseOrder = mode === 'new' ? 0 : rows.length;
          for (let i = 0; i < importedRows.length; i++) {
            await axios.post(
              `${API}/admin/workspace/inventory`,
              { node_id: nodeId, list_id: listId, order: baseOrder + i, ...importedRows[i] },
              auth,
            );
          }
          // Refresh from the server so newly-assigned ids hydrate the UI.
          await load();
        }}
      />
    </div>
  );
}

function ExportItem({ label, icon, onClick, testid, color }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-left hover:bg-blue-50 transition-colors"
      style={{ color, fontFamily: 'Manrope' }}
    >
      {icon} {label}
    </button>
  );
}

function InventoryCell({ value, editable, onSave, mobile = false, highlight, type, options, testid }) {
  const [draft, setDraft] = useState(value);
  const focusedRef = useRef(false);
  // Resync the draft when the parent's `value` changes — BUT not while the
  // user is actively typing. Without this guard, a sibling re-render that
  // happens between keystrokes (e.g. another row's save committing) would
  // clobber the in-flight typed text. This was the root cause of the
  // mobile "items disappear" bug — iOS auto-dismisses the keyboard before
  // firing `blur`, and any intervening parent re-render reset the draft.
  useEffect(() => {
    if (!focusedRef.current) setDraft(value);
  }, [value]);

  const cellBg = highlight || 'transparent';

  if (!editable) {
    if (type === 'select') {
      const opt = (options || []).find((o) => o.value === value);
      const label = opt ? opt.label : value;
      return (
        <div className={mobile ? 'text-sm' : 'px-3 py-2 border-r border-[#EEF3F9] truncate'} style={{ color: '#1A2E2E', backgroundColor: cellBg }} title={label}>
          {label || <span className="opacity-40">—</span>}
        </div>
      );
    }
    // iter-110 — Money columns get a $ prefix in the read-only view too,
    // so the unit is consistent whether you're viewing or editing.
    // iter-120 — also format with 2-decimal grouping if value is numeric.
    let moneyDisplay = value;
    if (type === 'money' && value) {
      const num = Number(String(value).replace(/[^0-9.\-]/g, ''));
      if (Number.isFinite(num)) {
        moneyDisplay = `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      } else {
        moneyDisplay = `$${value}`;
      }
    }
    const displayValue = (type === 'money' && value)
      ? moneyDisplay
      : (value || <span className="opacity-40">—</span>);
    return (
      <div className={mobile ? 'text-sm' : 'px-3 py-2 border-r border-[#EEF3F9] truncate'} style={{ color: '#1A2E2E', backgroundColor: cellBg }} title={value}>
        {displayValue}
      </div>
    );
  }

  if (type === 'select') {
    return (
      <select
        value={draft}
        onChange={(e) => { setDraft(e.target.value); onSave(e.target.value); }}
        className={mobile
          ? 'w-full text-sm px-2 py-1.5 rounded border outline-none'
          : 'w-full px-3 py-2 border-r border-[#EEF3F9] outline-none text-xs focus:bg-blue-50'
        }
        style={{ fontFamily: 'Manrope', borderColor: mobile ? '#D9E3F1' : undefined, backgroundColor: cellBg }}
      >
        {(options || []).map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
    );
  }

  return (
    <div className="relative w-full">
      {type === 'money' && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: mobile ? '8px' : '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: mobile ? '13px' : '11px',
            fontWeight: 600,
            color: '#7A8C8C',
            fontFamily: 'Manrope, sans-serif',
            pointerEvents: 'none',
            lineHeight: 1,
          }}
        >
          $
        </span>
      )}
      <input
        data-testid={testid}
        value={draft}
        onFocus={() => { focusedRef.current = true; }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          focusedRef.current = false;
          // iter-120 — Money cells get strict 2-decimal grouping on
          // blur so saved values display as e.g. 1,000,000.00.
          let next = draft;
          if (type === 'money' && next && String(next).trim() !== '') {
            const cleaned = String(next).replace(/[^0-9.\-]/g, '');
            const n = Number(cleaned);
            if (Number.isFinite(n)) {
              next = n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              if (next !== draft) setDraft(next);
            }
          }
          if (next !== value) onSave(next);
        }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        className={mobile
          ? 'w-full text-sm py-1.5 rounded border outline-none'
          : 'w-full py-2 border-r border-[#EEF3F9] outline-none text-xs focus:bg-blue-50'
        }
        style={{
          fontFamily: 'Manrope',
          borderColor: mobile ? '#D9E3F1' : undefined,
          backgroundColor: cellBg,
          paddingLeft: type === 'money' ? '20px' : (mobile ? '8px' : '12px'),
          paddingRight: mobile ? '8px' : '12px',
        }}
      />
    </div>
  );
}
